import { api, assertNever, isDefined } from 'common';

import { ScriptContextType } from './ScriptContext';
import { tryParseJson } from '../../../../../../../utils/json';

import DynamicValue = api.FlowDefinition.DynamicValue;
import DynamicValueType = api.FlowDefinition.DynamicValueType;

const { FlowKeywords } = api.FlowDefinition;

interface DynamicValueTypeOption {
  value: DynamicValueType;
  label: string;
  helperText: string;
}

const AutoTypeOption: DynamicValueTypeOption = {
  value: DynamicValueType.Auto,
  label: 'Auto',
  helperText: 'Either a placeholder or a fixed value specified here',
};

const IterationTypeOptions: readonly DynamicValueTypeOption[] = [
  {
    value: DynamicValueType.Element,
    label: 'Element',
    helperText: 'The current array element within the iteration',
  },
  {
    value: DynamicValueType.Block,
    label: 'Block',
    helperText: 'The data from a selected block',
  },
  {
    value: DynamicValueType.Expression,
    label: 'Reference',
    helperText: 'The value of a specified block, input, or subfield thereof',
  },
  {
    value: DynamicValueType.Literal,
    label: 'Constant',
    helperText: 'A fixed value specified here',
  },
];

const BaseTypeOptions = IterationTypeOptions.filter(
  ({ value }) => value !== DynamicValueType.Element
);

export function getDynamicValueTypeOptions(args: {
  scriptContextType: ScriptContextType | undefined;
  isAutoDynamicValueTypeEnabled: boolean;
  currentType?: DynamicValueType;
}): readonly DynamicValueTypeOption[] | undefined {
  const { scriptContextType, isAutoDynamicValueTypeEnabled, currentType } = args;
  const typeOptions =
    scriptContextType === ScriptContextType.Iteration ? IterationTypeOptions : BaseTypeOptions;
  if (isAutoDynamicValueTypeEnabled) {
    if (!isDefined(currentType) || currentType === DynamicValueType.Auto) {
      return undefined;
    }
    // Non-Auto type not yet converted
    return [AutoTypeOption, ...typeOptions];
  }
  if (currentType === DynamicValueType.Auto) {
    // Auto type for user without it enabled yet
    return [AutoTypeOption, ...typeOptions];
  }
  return typeOptions;
}

export function createDynamicValue(type: DynamicValueType | undefined): DynamicValue {
  if (!type) {
    return { type: undefined };
  }
  switch (type) {
    case DynamicValueType.Auto:
      return { type, jsonOrTemplate: '' };
    case DynamicValueType.Block:
      return { type, blockName: '' };
    case DynamicValueType.Element:
      return { type };
    case DynamicValueType.Expression:
      return { type, expression: '' };
    case DynamicValueType.Literal:
      return { type, value: undefined, json: '' };
    case DynamicValueType.Template:
      return { type, template: '' };
    default:
      assertNever('Invalid dynamic value type', type);
  }
}

export function convertDynamicValue(
  value: DynamicValue | undefined,
  newType: DynamicValueType | undefined
): DynamicValue {
  if (!isDefined(newType)) {
    return { type: undefined };
  }
  if (value) {
    const oldType = value.type;
    if (oldType === DynamicValueType.Auto && value.jsonOrTemplate) {
      const { jsonOrTemplate } = value;
      if (newType === DynamicValueType.Block) {
        const blockName = /^\{\{\s*(\w+)\.data\s*\}\}$/g.exec(jsonOrTemplate)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === DynamicValueType.Expression) {
        const expression = /^\{\{([^{}]*)\}\}$/g.exec(jsonOrTemplate)?.[1] ?? '';
        return { type: newType, expression };
      } else if (newType === DynamicValueType.Literal) {
        return { type: newType, json: jsonOrTemplate, value: tryParseJson(jsonOrTemplate).value };
      } else if (newType === DynamicValueType.Template) {
        return { type: newType, template: jsonOrTemplate };
      }
    } else if (oldType === DynamicValueType.Block && value.blockName) {
      if (newType === DynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${value.blockName}.data}}` };
      } else if (newType === DynamicValueType.Template) {
        return { type: newType, template: `{{${value.blockName}.data}}` };
      } else if (newType === DynamicValueType.Expression) {
        return { type: newType, expression: `${value.blockName}.data` };
      }
    } else if (oldType === DynamicValueType.Element) {
      if (newType === DynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${FlowKeywords.element}}}` };
      } else if (newType === DynamicValueType.Expression) {
        return { type: newType, expression: FlowKeywords.element };
      } else if (newType === DynamicValueType.Template) {
        return { type: newType, template: `{{${FlowKeywords.element}}}` };
      }
    } else if (oldType === DynamicValueType.Expression && value.expression) {
      if (newType === DynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${value.expression}}}` };
      } else if (newType === DynamicValueType.Block) {
        const blockName = /^\s*(\w+)\.data\s*$/g.exec(value.expression)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === DynamicValueType.Template) {
        return { type: newType, template: `{{${value.expression}}}` };
      }
    } else if (oldType === DynamicValueType.Literal && value.json) {
      if (newType === DynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: value.json };
      } else if (newType === DynamicValueType.Template) {
        const result = tryParseJson(value.json).value;
        return { type: newType, template: typeof result === 'string' ? result : value.json };
      }
    } else if (oldType === DynamicValueType.Template && value.template) {
      if (newType === DynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: value.template };
      } else if (newType === DynamicValueType.Block) {
        const blockName = /^\{\{\s*(\w+)\.data\s*\}\}$/g.exec(value.template)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === DynamicValueType.Expression) {
        const expression = /^\{\{([^{}]*)\}\}$/g.exec(value.template)?.[1] ?? '';
        return { type: newType, expression };
      } else if (newType === DynamicValueType.Literal) {
        return { type: newType, value: value.template, json: JSON.stringify(value.template) };
      }
    }
  }
  return createDynamicValue(newType);
}
